<template>
  <div class="table-container">
    <b-col
      sm="12"
      xs="12"
      class="d-flex justify-content-end align-items-center"
    >
      <slot name="dropdownfilter"></slot>
      <slot name="Add" v-if="addnewbtn"></slot>
      <span class="p-input-icon-left my-3 table-search">
        <i class="pi pi-search" />
        <InputText v-model="globalSearch" placeholder="Search" />
      </span>
    </b-col>
    <!-- {{ filteredData }} -->
    <DataTable
      :value="filteredData"
      v-model:selection="selectedProduct"
      editMode="cell"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      @update:selection="selectAllHandler"
      :metaKeySelection="true"
      @row-click="RowClick"
      @row-unselect-all="RowUnSelectAll"
      @row-unselect="RowUnSelect"
      @row-expand="onRowExpand"
      @row-collapse="onRowCollapse"
      v-model:expandedRows="expandedRows"
    >
      <template #empty>No data found.</template>

      <template>
        <Column
          expander
          style="width: 5rem"
          v-if="expandSubRows"
          class="field"
        />

        <Column header="#" headerStyle="width:3rem" v-if="seriaNlumber">
          <template #body="slotProps">
            {{ slotProps.index + 1 }}
          </template>
        </Column>

        <Column
          selectionMode="multiple"
          v-if="selectionMode"
          :class="CheckDisabled"
        >
          <template #body="{ data }">
            <Checkbox
              v-model="selectedProduct"
              :value="data"
              :disabled="data.disabled == true ? true : false"
              @change="(e) => handleRowSelection(data, e)"
            />
          </template>
        </Column>

        <Column
          v-for="column in columns"
          :key="column.field"
          :field="column.field"
          :header="column.header"
          :editable="column.editable"
          :slot="column.slot"
          :frozen="column.frozen"
          :frozenWidth="column.frozenWidth"
          :class="[column.class, column.frozencolumn ? 'columnfreeze' : '']"
          :selectionMode="column.selectionMode"
          :body-class="column.bodyclass"
          :hidden="column.hide"
        >
          <template #body="{ data, index }">
            <span
              v-if="column.fieldDisplay != 'false'"
              :data-header="column.header"
              class="body-data"
              :title="data[column.field]"
            >
              {{ data[column.field] }}
            </span>
            <span v-else :data-header="column.header" class="body-data">
              <slot
                :name="column.slot"
                :data="data"
                :header="column.header"
                :row-index="index"
                :on-change="
                  (newValue) => updatedata(newValue, index, column.field)
                "
              >
              </slot>
            </span>
          </template>
        </Column>
      </template>
      <template #expansion="slotProps">
        <DataTable :value="SubList" responsiveLayout="scroll" class="py-4">
          <Column
            v-for="subCol in subColumns"
            :key="subCol.field"
            :field="subCol.field"
            :header="subCol.header"
          />
        </DataTable>
      </template>

      <template #footer>
        <slot name="Footer"></slot>
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, watch } from "vue";
import { debounce } from "lodash";
import { boolean } from "yup";

export default defineComponent({
  name: "TallyTable",
  props: {
    value: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    selection: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    columns: {
      type: Array as PropType<any[]>,
      required: true,
    },
    selectionMode: {
      type: Boolean,
      default: false,
    },
    addnewbtn: {
      type: Boolean,
      default: false,
    },
    footerVisible: {
      type: Boolean,
      default: false,
    },
    seriaNlumber: {
      type: Boolean,
      default: false,
    },
    CheckDisabled: {
      type: String,
      default: "true",
    },
    FromPage: {
      type: String,
      default: "",
    },
    expandSubRows: {
      type: Boolean,
      default: false,
    },
    SubList: {
      type: Array,
      default: () => [],
    },
    subColumns: {
      type: Array as PropType<any[]>,
      required: false,
    },
  },
  setup(props, { emit }) {
    const internalValue = ref(props.value);
    const selectedProduct = ref<any[]>(props.selection);
    const globalSearch = ref("");

    watch(
      () => props.value,
      (newVal) => {
        internalValue.value = newVal;
      },
      { immediate: true }
    );

    watch(
      () => internalValue.value,
      (newVal) => {
        emit("update:value", newVal);
      }
    );

    watch(
      () => props.selection,
      (newVal) => {
        selectedProduct.value = newVal;
      },
      { immediate: true }
    );

    watch(
      () => selectedProduct.value,
      (newVal) => {
        emit("update:selection", newVal);
      }
    );

    const filteredData = computed(() => {
      if (!globalSearch.value) return internalValue.value;
      return internalValue.value.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(globalSearch.value.toLowerCase())
        )
      );
    });

    function updatedata(newValue: any, rowIndex: number, field: string) {
      internalValue.value[rowIndex][field] = newValue;
      emit("update:value", internalValue.value);
    }

    function selectAllHandler(event: any) {
      if (event && event.length > 0) {
        // Get all unique VoucherNos from selected items
        const selectedVoucherNos = new Set(
          event.map((item: any) => item.VoucherNo)
        );
        // Select all rows with those VoucherNos
        selectedProduct.value = internalValue.value.filter((item: any) =>
          selectedVoucherNos.has(item.VoucherNo)
        );
      } else {
        selectedProduct.value = [];
      }
      emit("update:selection", selectedProduct.value);
    }

    function handleRowSelection(row: any, event: any) {
      const checked = event.target.checked;

      // Find all rows with the same VoucherNo
      const relatedRows = internalValue.value.filter(
        (item) => item.VoucherNo === row.VoucherNo
      );

      if (checked) {
        // Add all related rows that aren't already selected
        relatedRows.forEach((relatedRow) => {
          if (!selectedProduct.value.includes(relatedRow)) {
            selectedProduct.value.push(relatedRow);
          }
        });
      } else {
        // Remove all related rows
        selectedProduct.value = selectedProduct.value.filter(
          (item) => item.VoucherNo !== row.VoucherNo
        );
      }

      emit("update:selection", selectedProduct.value);
    }

    function RowClick(event: any) {
      emit("row-click", event);
    }

    function RowUnSelectAll(event: any) {
      emit("row-unselect-all", event);
    }

    function RowUnSelect(event: any) {
      emit("row-unselect", event);
    }

    const expandedRows: any = ref([]);

    const onRowExpand = (event: any) => {
      expandedRows.value = [event.data];
      emit("expandRows", event.data);
    };

    const onRowCollapse = (event: any) => {
      expandedRows.value = [];
      emit("collapseRows", event.data);
    };

    return {
      internalValue,
      selectedProduct,
      globalSearch,
      filteredData,
      updatedata,
      selectAllHandler,
      handleRowSelection,
      RowClick,
      RowUnSelectAll,
      RowUnSelect,
      onRowExpand,
      onRowCollapse,
      expandedRows,
    };
  },
});
</script>

<style lang="scss">
.table-container {
  .p-datatable {
    .p-datatable-tbody {
      tr {
        &:nth-child(odd) {
          background-color: #f2f2f2;
        }

        td {
          vertical-align: middle;
          border-color: #c5c5c5;
        }
      }
    }

    .p-datatable-thead {
      background-color: #4caf50;
      color: #fff;
      font-weight: bold;

      th {
        color: rgb(31, 4, 4) !important;
        height: 50px;
        vertical-align: middle;
        border-color: #c5c5c5;

        &:first-child {
          border-top-left-radius: 5px;
        }

        &:last-child {
          border-top-right-radius: 5px;
        }
      }
    }
  }

  .disableclass {
    pointer-events: none;
    opacity: 0.5;
  }
}

.columnfreeze {
  position: sticky;
  right: -16px;
  z-index: 1;
  width: 40px !important;
  background-color: #e1e1e1;
}

.columnfreezeright {
  position: sticky;
  right: 10px;
  z-index: 1;
  width: 40px !important;
  background-color: #e1e1e1;
}

.columnfreezeleft {
  position: sticky;
  left: -16px;
  z-index: 1;
  width: 40px !important;
  background-color: #e1e1e1;
}

.coumnfreezeleft1 {
  position: sticky;
  left: -16px;
  z-index: 1;
  width: 40px !important;
  background-color: #e1e1e1;
}

.coumnfreezeleft2 {
  position: sticky;
  left: 73px;
  z-index: 1;
  width: 40px !important;
  background-color: #e1e1e1;
}

.coumnfreezeleft3 {
  position: sticky;
  left: 160px;
  z-index: 1;
  width: 140px !important;
  background-color: #e1e1e1;
}
.coumnfreezeleft4 {
  position: sticky;
  left: 300px;
  z-index: 1;
  width: 140px !important; /* Adjust width as needed */
  background-color: #e1e1e1;
}
</style>
